import { Observable, of, Subscription } from 'rxjs';
import { map, catchError, debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { PasswordModule } from 'primeng/password';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { WorkerDialogService } from './worker-dialog.service';
import { ViewScopeService } from '../../../services/view-scope.service';

import { SelectItem } from '../../../models/selected-item.model';

import { TranslateService } from '../../../services/translate.service';

import { WorkerBasicInfoCreate, WorkerBasicInfoEdit } from '../../../models/workers-general-record.model';
import { DirectionService } from '../../../services/direction.service';


@Component({
	selector: 'app-worker-dialog',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		TooltipModule,
		ReactiveFormsModule,
		DialogModule,
		ToastModule,
		InputTextModule,
		AutoCompleteModule,
		PasswordModule,
		ButtonModule
	],
	templateUrl: './worker-dialog.component.html',
	styleUrls: ['./worker-dialog.component.scss'],
	providers: [MessageService]
})
export class WorkerDialogComponent implements OnInit, OnDestroy, OnChanges {

	@Input() mode: 'create' | 'edit' = 'create';
	@Input() visible: boolean = false;
	@Input() data?: WorkerBasicInfoEdit | WorkerBasicInfoCreate | null = null;
	@Input() useDialog: boolean = true;
	@Input() phonePrefixes: any[] = [];

	@Output() closed = new EventEmitter<void>();
	@Output() visibleChange = new EventEmitter<boolean>();
	@Output() saved = new EventEmitter<any>();


	readonly emailRegex = "^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$";
    readonly hebrewRegex = '^[\\u0590-\\u05FF\\s-]+$';

	roleOptions : SelectItem[] = [];
	filteredRoleOptions: SelectItem[] = [];

	form: FormGroup;
	tempPassword: string = "";

	originalUserName: string | null = null;
	originalEmail: string | null = null;

	editingUserRecID: number | null = null;

	private _subs: Subscription[] = [];

	selectedRole: SelectItem | null = null;
	selectedPhonePref: any | null = null;


	filteredPhonePrefixes: any[] = [];


	constructor(
		private messageService: MessageService,
		public translateServ: TranslateService,
		public dirServ: DirectionService,
		private workerDialogService: WorkerDialogService,
		private viewScopeService: ViewScopeService,
		private fb: FormBuilder
	) 
	{
		this.setRolesOptions().pipe(
			catchError(() => of([]))
		).subscribe((opts: SelectItem[]) => 
		{
			this.roleOptions = opts || [];
			this.filteredRoleOptions = [...this.roleOptions];
			// after roles are loaded, try to apply any pending incoming data (to resolve Role selection)
			this.applyIncomingData(this.data);
		});
		
		this.form = this.fb.group({
			FirstName: ['', Validators.required],
			LastName: ['', Validators.required],
			UserName: ['', Validators.required],
			Pass: [''],
			Role: ['', Validators.required],
			Notes: [''],
			PhonePref: [''],
			PhonePrefRecID: [''],
			PhoneNumber: ['', Validators.pattern('^[0-9]{7}$')],
			FirstName_HE: ['', [Validators.required, Validators.pattern(this.hebrewRegex)]],
			LastName_HE: ['', [Validators.required, Validators.pattern(this.hebrewRegex)]],
			Email: ['', [Validators.required, Validators.pattern(this.emailRegex)]]
		});
	}

	ngOnInit(): void {
		// Ensure password control disabled/enabled state reflects current `mode` at init
		this.updatePasswordValidator();
		const userCtrl = this.form.get('UserName');
		if (userCtrl) {
			const sub = userCtrl.valueChanges.pipe(
				debounceTime(400),
				distinctUntilChanged(),
				switchMap((val: any) => {
					if (!val) return of(false);
					// In edit mode, if value is unchanged from incoming, skip check
					if (this.mode === 'edit' && val === (this.originalUserName || '')) return of(false);
					return this.workerDialogService.getIsUserNameExsist(val).pipe(catchError(() => of(false)));
				})
			).subscribe((exists: boolean | null) => {
				const ctrl = this.form.get('UserName');
				if (!ctrl) return;
				if (exists) {
					ctrl.setErrors(Object.assign({}, ctrl.errors || {}, { exists: true }));
				} else {
					if (ctrl.errors) {
						delete (ctrl.errors as any).exists;
						if (Object.keys(ctrl.errors).length === 0) ctrl.setErrors(null);
					}
				}
			});
			this._subs.push(sub);
		}

		const emailCtrl = this.form.get('Email');
		if (emailCtrl) {
			const sub2 = emailCtrl.valueChanges.pipe(
				debounceTime(400),
				distinctUntilChanged(),
				switchMap((val: any) => {
					if (!val) return of(false);
					// Skip check if unchanged in edit mode
					if (this.mode === 'edit' && val === (this.originalEmail || '')) return of(false);
					return this.workerDialogService.getIsEmailExsist(val).pipe(catchError(() => of(false)));
				})
			).subscribe((exists: boolean | null) => {
				const ctrl = this.form.get('Email');
				if (!ctrl) return;
				if (exists) {
					ctrl.setErrors(Object.assign({}, ctrl.errors || {}, { exists: true }));
				} else {
					if (ctrl.errors) {
						delete (ctrl.errors as any).exists;
						if (Object.keys(ctrl.errors).length === 0) ctrl.setErrors(null);
					}
				}
			});
			this._subs.push(sub2);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['mode']) {
			this.updatePasswordValidator();
		}

		if (changes['data']) {
			// attempt to apply incoming data to the form (maps different field names)
			this.applyIncomingData(this.data);
		}

		if (changes['phonePrefixes'] && this.phonePrefixes) {
			this.filteredPhonePrefixes = [...this.phonePrefixes];
			// phone prefixes may enable resolving a pending phone from incoming data
			this.applyIncomingData(this.data);
		}
	}

	/**
	 * Map incoming `data` (possibly from workers$ with different field names) into the form.
	 * - handles FirstNameEN / FirstNameHE variants
	 * - resolves Role by RoleRecID into `selectedRole` and patches `Role` form control
	 * - splits Phones into prefix and number and resolves PhonePrefRecID via `phonePrefixes`
	 */
	private applyIncomingData(data: any | null | undefined) {
		if (!data) return;

		const incoming: any = data as any;
		const patch: any = {};

		// Map English names
		patch.FirstName = incoming.FirstNameEN || '';
		patch.LastName =  incoming.LastNameEN  || '';

		// Map Hebrew names
		patch.FirstName_HE = incoming.FirstNameHE || '';
		patch.LastName_HE  = incoming.LastNameHE  || '';

		// User / Email
		patch.UserName 	= incoming.UserName || '';
		patch.Email 	= incoming.Emails	|| '';

		this.form.patchValue(patch);

		// Remember original values so we can skip existence-checks when unchanged in edit mode
		this.originalUserName = incoming.UserName || null;
		this.originalEmail = incoming.Emails || null;

		// Keep the UserRecID for edit operations so we can send it back on save
		this.editingUserRecID = incoming.UserRecID ?? null;

		// Resolve role by RoleRecID (may rely on roleOptions being populated)
		const roleRecId = incoming.RoleRecID || null;

		if (roleRecId && (this.roleOptions || []).length > 0) 
		{
			const found = (this.roleOptions || []).find(r => r.code == roleRecId || (r as any).RoleRecID == roleRecId);
			if (found) 
			{
				this.selectedRole = found as SelectItem;
				this.form.patchValue({ Role: this.selectedRole.label || this.selectedRole.value });
			}
		}

		// Phones: split prefix-number by '-' if present
		const phonesRaw = incoming.Phones || '';
		if (phonesRaw && typeof phonesRaw === 'string') 
		{
			const parts = phonesRaw.split('-');
			let pref = parts[0];
			let number = parts.slice(1).join('-');

			this.form.patchValue({ PhoneNumber: number, PhonePref: pref });

			// resolve PhonePrefRecID when phonePrefixes are available
			if ((this.phonePrefixes || []).length > 0) 
			{
				const pf = (this.phonePrefixes || []).find((p: any) => p.label === pref || p.value === pref);
				if (pf) 
				{
					this.selectedPhonePref = pf;
					this.form.patchValue({ PhonePrefRecID: pf.code ?? pf.PhonePrefRecID ?? pf.code });
				}
			}
		}
	}


	filterPhonePrefixes(event: any) {
		const query = event.query ? event.query.toLowerCase() : '';
		this.filteredPhonePrefixes = (this.phonePrefixes || []).filter(p => (p.label || '').toLowerCase().includes(query));
	}

	onPhonePrefSelect(event: any) {
		const candidate = event && (event.value !== undefined) ? event.value : event;
		const sel = candidate && (candidate.label || candidate.value) ? candidate : null;
		// If PrimeNG passes only a primitive (label/value), try to find the full object in phonePrefixes
		let pf = sel;
		if (!pf && candidate) {
			pf = (this.phonePrefixes || []).find(p => p.label === candidate || p.value === candidate) || null;
		}
		this.selectedPhonePref = pf || null;
		if (this.selectedPhonePref) {
			this.form.patchValue({ PhonePrefRecID: this.selectedPhonePref.code ?? this.selectedPhonePref.PhonePrefRecID ?? this.selectedPhonePref.code, PhonePref: this.selectedPhonePref.label || this.selectedPhonePref.value });
		}
	}

	filterRoleOptions(event: any) {
		const query = event.query ? event.query.toLowerCase() : '';
		this.filteredRoleOptions = (this.roleOptions || []).filter(r => (r.label || '').toLowerCase().includes(query));
	}

	onRoleSelect(event: any) {
		// PrimeNG AutoComplete may pass either the selected object or an event with a `value` property
		const candidate = event && (event.value !== undefined) ? event.value : event;
		const sel = candidate && (candidate.label || candidate.value) ? candidate : null;
		this.selectedRole = sel as SelectItem | null;
		if (this.selectedRole) {
			this.form.patchValue({ Role: this.selectedRole.label || this.selectedRole.value });
		}
	}

	private updatePasswordValidator() 
	{
		const passControl = this.form.get('Pass');
		if (!passControl) return;

		if (this.mode === 'create') {
			passControl.enable({ emitEvent: false });
			passControl.setValidators([Validators.required, Validators.minLength(6)]);
		} else {
			// In edit mode the password is not required and should be disabled
			passControl.clearValidators();
			passControl.setValue(null, { emitEvent: false });
			passControl.disable({ emitEvent: false });
		}
		passControl.updateValueAndValidity({ emitEvent: false });
	}

	save(): void 
	{
			if (this.form.invalid)
			{
				this.form.markAllAsTouched();
				return;
			}

			const raw = this.form.value as any;
			// Build payload similarly to BasicInfoComponent: normalize codes/recids and format fields
			const payload: any = {};

			// Map simple textual fields
			payload.FirstName = raw.FirstName || '';
			payload.LastName = raw.LastName || '';
			payload.FirstName_HE = raw.FirstName_HE || '';
			payload.LastName_HE = raw.LastName_HE || '';
			payload.UserName = raw.UserName || '';

			// Map phone number
			if (raw.PhoneNumber) {
				payload.Phone = Number(raw.PhoneNumber);
			} else {
				payload.Phone = null;
			}

			// Resolve PhonePrefRecID
			if (raw.PhonePrefRecID) {
				payload.PhonePrefRecID = raw.PhonePrefRecID;
			} else if (raw.PhonePref) {
				const pf = (this.phonePrefixes || []).find((p: any) => p.label === raw.PhonePref || p.value === raw.PhonePref);
				payload.PhonePrefRecID = pf ? (pf.code ?? pf.PhonePrefRecID ?? pf.code) : null;
			} else {
				payload.PhonePrefRecID = null;
			}

			// Resolve RoleRecID
			const ro = this.selectedRole ?? (raw.Role ? (this.roleOptions || []).find(r => r.label === raw.Role || r.value === raw.Role) : null);
			payload.RoleRecID = ro ? (ro.code ?? (ro as any).RoleRecID ?? ro.code) : null;

			// Notes/Descr mapping
			payload.Descr = raw.Notes || raw.Descr || null;
			
			if(this.mode === "create" ) payload.Pass = raw.Pass || null;
			payload.UserName = raw.UserName || null;
			payload.Email = raw.Email || null;

			// Meta fields
			payload.opUserRecID = this.viewScopeService.getCurrentOpertaingUserId();
			if(this.mode === "create" ) payload.CompanyRecID = this.viewScopeService.getSelectedCompanyId();
			if (this.mode === 'edit') payload.UserRecID = this.editingUserRecID ;

			// Clean up and call service
			this.workerDialogService.changesForWorker(this.mode === 'create' ? 0 : 1, payload);

			this.messageService.add({
				severity: 'success',
				summary: this.mode === 'create' ? (this.translateServ.dictionary['WORKER_CREATED'] || 'Worker Created') : (this.translateServ.dictionary['WORKER_UPDATED'] || 'Worker Updated'),
				detail: this.mode === 'create' ? (this.translateServ.dictionary['WORKER_CREATED_DETAIL'] || 'New worker saved') : (this.translateServ.dictionary['WORKER_UPDATED_DETAIL'] || 'Worker changes saved'),
				life: 3000
			});
				// Notify parent that a save occurred
				this.saved.emit(payload);
				this.closeDialog();
	}

	cancel(): void {
		this.closeDialog();
		this.messageService.add({ severity: 'info', summary: this.translateServ.dictionary['CANCELLED'] || 'Cancelled', detail: this.translateServ.dictionary['ACTION_CANCELLED'] || 'Action cancelled', life: 2000 });
	}

	private closeDialog(): void {
		this.visible = false;
		this.visibleChange.emit(this.visible);
		this.closed.emit();
	}


	public createTempPass() {
		// Do nothing when editing — password generation applies only in create mode
		if (this.form.get('Pass')?.disabled) return;

		this.workerDialogService.getSuggestedPassword().subscribe({
			next: (data: any) => {
				// 1. Ensure we parse out the plain string if 'data' is wrapped in an object
				const generatedPassword = typeof data === 'object' ? (data.password || JSON.stringify(data)) : data;

				// 2. Set the temporary variable
				this.tempPassword = generatedPassword;
				console.log('Temporary password generated:', this.tempPassword);

				// 3. Patch the PrimeNG password input form control directly
				this.form.patchValue({ Pass: this.tempPassword });
				this.form.get('Pass')?.markAsDirty(); // Informs validation it changed

				// 4. Securely wipe it from the TS variable on the next microtask loop
				setTimeout(() => {
					this.tempPassword = "";
					console.log('tempPassword has been securely wiped from TS memory:', this.tempPassword);
				}, 0);
			},
			error: (err) => console.error('Error receiving generated password:', err)
		});
	}

	private setRolesOptions(): Observable<SelectItem[]> 
	{
		const isRtl = this.dirServ.isRtl();
		return this.workerDialogService.getRoles().pipe(
			map((response: any) => {
				return (response || []).map((item: any) => 
				(
				{
					label: isRtl ?  item.RoleName : item.Role, 
					value: isRtl ?  item.RoleName : item.Role, 
					code: item.RoleRecID
				})) as SelectItem[];
			})
		);
	}

	ngOnDestroy(): void {
		(this._subs || []).forEach(s => { try { s.unsubscribe(); } catch { } });
	}

}
